import { clamp, clamp01 } from '../core/math'
import { SNOW_TYPE_COUNT, Snow } from './snow'

/**
 * A regular-grid heightfield in world space.
 *
 * World axes: +X is looker's right across the face, +Y is up, +Z is downhill.
 * The grid origin sits at (minX, minZ) and spans `cell` metres per step.
 *
 * Everything downstream — physics, mesh build, snow, camera, solver, scoring —
 * samples through this one object, so there is exactly one definition of where
 * the ground is and what it is made of.
 */
export class Heightfield {
  readonly cols: number
  readonly rows: number
  readonly height: Float32Array

  /** Per-cell snow weights, SNOW_TYPE_COUNT per cell, normalised. */
  readonly snowWeights: Float32Array

  /** 0..1 mask of exposed rock — no grip, and rendered as rock. */
  readonly rock: Float32Array

  /** Local concavity (negative = convex rollover, positive = gully). */
  readonly concavity: Float32Array

  /**
   * Consequence of falling here, 0..1. Spec §4: "A 45° slope with a clean
   * runout is worth less than a 42° slope above a cliff." Computed by the
   * hazard pass by looking downhill for cliffs and traps.
   */
  readonly exposure: Float32Array

  /** Terrain-trap membership, 0..1 — gully bottoms and flat ground under steep walls. */
  readonly trap: Float32Array

  /** How readily this cell releases sluff, 0..1 (spec §6). */
  readonly sluffProne: Float32Array

  constructor(
    readonly minX: number,
    readonly minZ: number,
    readonly width: number,
    readonly length: number,
    readonly cell: number,
  ) {
    this.cols = Math.round(width / cell) + 1
    this.rows = Math.round(length / cell) + 1
    const n = this.cols * this.rows
    this.height = new Float32Array(n)
    this.snowWeights = new Float32Array(n * SNOW_TYPE_COUNT)
    this.rock = new Float32Array(n)
    this.concavity = new Float32Array(n)
    this.exposure = new Float32Array(n)
    this.trap = new Float32Array(n)
    this.sluffProne = new Float32Array(n)
    // Default to powder everywhere so a partially-built field is still valid.
    for (let i = 0; i < n; i++) this.snowWeights[i * SNOW_TYPE_COUNT + Snow.Powder] = 1
  }

  get maxX(): number { return this.minX + this.width }
  get maxZ(): number { return this.minZ + this.length }

  index(ix: number, iz: number): number { return iz * this.cols + ix }
  xAt(ix: number): number { return this.minX + ix * this.cell }
  zAt(iz: number): number { return this.minZ + iz * this.cell }

  /** Nearest in-bounds height, no interpolation. */
  heightAtIndex(ix: number, iz: number): number {
    const cx = clamp(ix, 0, this.cols - 1) | 0
    const cz = clamp(iz, 0, this.rows - 1) | 0
    return this.height[cz * this.cols + cx]
  }

  /**
   * Bilinear height sample. Out-of-bounds clamps to the edge, which combined
   * with the face's raised rim means a skier who leaves the face runs up
   * against a wall rather than falling through the world.
   */
  sampleHeight(x: number, z: number): number {
    const gx = clamp((x - this.minX) / this.cell, 0, this.cols - 1.0001)
    const gz = clamp((z - this.minZ) / this.cell, 0, this.rows - 1.0001)
    const ix = gx | 0
    const iz = gz | 0
    const fx = gx - ix
    const fz = gz - iz
    const i = iz * this.cols + ix
    const h00 = this.height[i]
    const h10 = this.height[i + 1]
    const h01 = this.height[i + this.cols]
    const h11 = this.height[i + this.cols + 1]
    const a = h00 + (h10 - h00) * fx
    const b = h01 + (h11 - h01) * fx
    return a + (b - a) * fz
  }

  /** Generic bilinear sample of any single-channel field on this grid. */
  sampleField(field: Float32Array, x: number, z: number): number {
    const gx = clamp((x - this.minX) / this.cell, 0, this.cols - 1.0001)
    const gz = clamp((z - this.minZ) / this.cell, 0, this.rows - 1.0001)
    const ix = gx | 0
    const iz = gz | 0
    const fx = gx - ix
    const fz = gz - iz
    const i = iz * this.cols + ix
    const a = field[i] + (field[i + 1] - field[i]) * fx
    const b = field[i + this.cols] + (field[i + this.cols + 1] - field[i + this.cols]) * fx
    return a + (b - a) * fz
  }

  /** Bilinear sample of the four snow weights into `out`. */
  sampleSnowWeights(x: number, z: number, out: Float32Array): void {
    const gx = clamp((x - this.minX) / this.cell, 0, this.cols - 1.0001)
    const gz = clamp((z - this.minZ) / this.cell, 0, this.rows - 1.0001)
    const ix = gx | 0
    const iz = gz | 0
    const fx = gx - ix
    const fz = gz - iz
    const w = this.snowWeights
    const i00 = (iz * this.cols + ix) * SNOW_TYPE_COUNT
    const i10 = i00 + SNOW_TYPE_COUNT
    const i01 = ((iz + 1) * this.cols + ix) * SNOW_TYPE_COUNT
    const i11 = i01 + SNOW_TYPE_COUNT
    let sum = 0
    for (let k = 0; k < SNOW_TYPE_COUNT; k++) {
      const a = w[i00 + k] + (w[i10 + k] - w[i00 + k]) * fx
      const b = w[i01 + k] + (w[i11 + k] - w[i01 + k]) * fx
      const v = a + (b - a) * fz
      out[k] = v
      sum += v
    }
    if (sum > 1e-6) for (let k = 0; k < SNOW_TYPE_COUNT; k++) out[k] /= sum
  }

  /** Write normalised weights into a cell. */
  setSnow(i: number, weights: ArrayLike<number>): void {
    let sum = 0
    for (let k = 0; k < SNOW_TYPE_COUNT; k++) sum += weights[k]
    const inv = sum > 1e-6 ? 1 / sum : 0
    const o = i * SNOW_TYPE_COUNT
    for (let k = 0; k < SNOW_TYPE_COUNT; k++) this.snowWeights[o + k] = weights[k] * inv
  }

  /**
   * Surface normal via central differences on the bilinear surface.
   * Writes into `out` to keep the physics step allocation-free.
   */
  sampleNormal(x: number, z: number, out: { x: number; y: number; z: number }): void {
    const e = this.cell
    const hL = this.sampleHeight(x - e, z)
    const hR = this.sampleHeight(x + e, z)
    const hD = this.sampleHeight(x, z - e)
    const hU = this.sampleHeight(x, z + e)
    const dhdx = (hR - hL) / (2 * e)
    const dhdz = (hU - hD) / (2 * e)
    const nx = -dhdx
    const ny = 1
    const nz = -dhdz
    const inv = 1 / Math.sqrt(nx * nx + ny * ny + nz * nz)
    out.x = nx * inv
    out.y = ny * inv
    out.z = nz * inv
  }

  /** Slope angle in radians, 0 = flat. */
  slopeAngleAt(x: number, z: number): number {
    const e = this.cell
    const dhdx = (this.sampleHeight(x + e, z) - this.sampleHeight(x - e, z)) / (2 * e)
    const dhdz = (this.sampleHeight(x, z + e) - this.sampleHeight(x, z - e)) / (2 * e)
    return Math.atan(Math.sqrt(dhdx * dhdx + dhdz * dhdz))
  }

  /**
   * Compass aspect in radians: the direction the slope faces, measured from
   * -Z (which the generator treats as north) clockwise. Drives the snow layer.
   */
  aspectAt(x: number, z: number): number {
    const e = this.cell
    const dhdx = (this.sampleHeight(x + e, z) - this.sampleHeight(x - e, z)) / (2 * e)
    const dhdz = (this.sampleHeight(x, z + e) - this.sampleHeight(x, z - e)) / (2 * e)
    // Downslope direction is (dhdx, dhdz) negated; aspect faces downslope.
    return Math.atan2(-dhdx, dhdz)
  }

  sampleRock(x: number, z: number): number { return clamp01(this.sampleField(this.rock, x, z)) }
  sampleExposure(x: number, z: number): number { return clamp01(this.sampleField(this.exposure, x, z)) }
  sampleTrap(x: number, z: number): number { return clamp01(this.sampleField(this.trap, x, z)) }
  sampleSluffProne(x: number, z: number): number { return clamp01(this.sampleField(this.sluffProne, x, z)) }
  sampleConcavity(x: number, z: number): number { return this.sampleField(this.concavity, x, z) }

  inBounds(x: number, z: number): boolean {
    return x >= this.minX && x <= this.maxX && z >= this.minZ && z <= this.maxZ
  }

  /**
   * Discrete Laplacian of the height, for render shading, the camera's crest
   * anticipation, and the hazard pass. Negative on convex rollovers, positive
   * in gullies and compressions.
   */
  computeConcavity(): void {
    const { cols, rows, height, concavity, cell } = this
    const inv = 1 / (cell * cell)
    for (let iz = 0; iz < rows; iz++) {
      for (let ix = 0; ix < cols; ix++) {
        const i = iz * cols + ix
        const c = height[i]
        const l = height[iz * cols + Math.max(ix - 1, 0)]
        const r = height[iz * cols + Math.min(ix + 1, cols - 1)]
        const d = height[Math.max(iz - 1, 0) * cols + ix]
        const u = height[Math.min(iz + 1, rows - 1) * cols + ix]
        concavity[i] = (l + r + d + u - 4 * c) * inv
      }
    }
  }

  elevationRange(): { min: number; max: number } {
    let min = Infinity
    let max = -Infinity
    for (let i = 0; i < this.height.length; i++) {
      const h = this.height[i]
      if (h < min) min = h
      if (h > max) max = h
    }
    return { min, max }
  }
}
